import React from 'react';
import { Workflow, BookOpen, BrainCircuit, Users, Bot, Sparkles } from 'lucide-react';

/**
 * ContextModelClip
 * A 16:9 animated visualization for the "missing piece" section:
 *  - Left: three fragmented sources (Process data, Business knowledge, Intelligence)
 *    pulse and stream particles toward the center.
 *  - Center: a hexagonal "Context Model" core absorbs the streams, rotating &
 *    glowing as it gains operational clarity.
 *  - Right: People + Agents nodes receive clarity; an outcome tagline cycles:
 *    "Reason correctly" → "Decide sensibly" → "Act reliably".
 */

const Source = ({ Icon, label, sub, top, delay }) => (
  <div
    className="absolute left-0 right-0 flex items-center gap-3 cmc-source"
    style={{ top, animationDelay: `${delay}s` }}
  >
    <div className="w-10 h-10 rounded-xl bg-neutral-950 border border-neutral-800 flex items-center justify-center flex-shrink-0">
      <Icon className="w-4 h-4 text-blue-300" />
    </div>
    <div className="min-w-0">
      <div className="text-[12.5px] font-semibold text-white whitespace-nowrap">{label}</div>
      <div className="text-[10.5px] text-neutral-500 whitespace-nowrap">{sub}</div>
    </div>
  </div>
);

const ContextModelClip = () => {
  return (
    <div className="relative w-full aspect-[5/6] sm:aspect-video rounded-2xl overflow-hidden border border-neutral-800 bg-gradient-to-br from-neutral-950 via-black to-neutral-950" data-testid="context-model-clip">
      <style>{`
        @keyframes cmc-bg {
          0%   { transform: translate(0, 0); }
          50%  { transform: translate(10px, -8px); }
          100% { transform: translate(0, 0); }
        }
        @keyframes cmc-rot   { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        @keyframes cmc-rot-r { 0% { transform: rotate(0deg); } 100% { transform: rotate(-360deg); } }
        @keyframes cmc-ring  {
          0%   { transform: scale(0.9); opacity: 0.6; }
          80%  { transform: scale(1.6); opacity: 0;   }
          100% { transform: scale(1.6); opacity: 0;   }
        }
        @keyframes cmc-glow  {
          0%, 100% { filter: drop-shadow(0 0 12px rgba(59,130,246,0.35)); }
          50%      { filter: drop-shadow(0 0 22px rgba(59,130,246,0.55)); }
        }
        @keyframes cmc-source {
          0%, 100% { opacity: 0.6; transform: translateX(0); }
          50%      { opacity: 1;   transform: translateX(2px); }
        }
        @keyframes cmc-dot {
          0%, 100% { opacity: 0.45; transform: scale(1); }
          50%      { opacity: 1;    transform: scale(1.2); }
        }
        @keyframes cmc-tag-1 { 0%, 28% { opacity: 1; transform: translateY(0); } 34%, 100% { opacity: 0; transform: translateY(-4px); } }
        @keyframes cmc-tag-2 { 0%, 32% { opacity: 0; transform: translateY(4px); } 38%, 60% { opacity: 1; transform: translateY(0); } 66%, 100% { opacity: 0; transform: translateY(-4px); } }
        @keyframes cmc-tag-3 { 0%, 64% { opacity: 0; transform: translateY(4px); } 70%, 100% { opacity: 1; transform: translateY(0); } }
        @keyframes cmc-scan  {
          0%   { transform: translateY(-100%); opacity: 0; }
          15%  { opacity: 0.45; }
          85%  { opacity: 0.45; }
          100% { transform: translateY(100%); opacity: 0; }
        }
        @keyframes cmc-pkt-a { 0% { offset-distance: 0%; opacity: 0; } 8% { opacity: 1; } 95% { offset-distance: 100%; opacity: 1; } 100% { opacity: 0; } }
        @keyframes cmc-pkt-b { 0%, 12% { offset-distance: 0%; opacity: 0; } 20% { opacity: 1; } 95% { offset-distance: 100%; opacity: 1; } 100% { opacity: 0; } }
        @keyframes cmc-pkt-c { 0%, 24% { offset-distance: 0%; opacity: 0; } 30% { opacity: 1; } 95% { offset-distance: 100%; opacity: 1; } 100% { opacity: 0; } }
        @keyframes cmc-out-a { 0%, 50% { offset-distance: 0%; opacity: 0; } 55% { opacity: 1; } 100% { offset-distance: 100%; opacity: 1; } }
        @keyframes cmc-out-b { 0%, 62% { offset-distance: 0%; opacity: 0; } 68% { opacity: 1; } 100% { offset-distance: 100%; opacity: 1; } }
        .cmc-bg   { animation: cmc-bg 12s ease-in-out infinite; }
        .cmc-rot  { animation: cmc-rot 18s linear infinite; transform-origin: center; }
        .cmc-rot-r{ animation: cmc-rot-r 22s linear infinite; transform-origin: center; }
        .cmc-ring { animation: cmc-ring 3.6s ease-out infinite; transform-origin: center; }
        .cmc-glow { animation: cmc-glow 3.6s ease-in-out infinite; }
        .cmc-source { animation: cmc-source 3.6s ease-in-out infinite; }
        .cmc-dot { animation: cmc-dot 1.8s ease-in-out infinite; transform-origin: center; transform-box: fill-box; }
        .cmc-tag-1 { animation: cmc-tag-1 9s linear infinite; }
        .cmc-tag-2 { animation: cmc-tag-2 9s linear infinite; }
        .cmc-tag-3 { animation: cmc-tag-3 9s linear infinite; }
        .cmc-scan  { animation: cmc-scan 6s ease-in-out infinite; }
        .cmc-pkt   { will-change: offset-distance, opacity; }
        .cmc-pkt-a { animation: cmc-pkt-a 4.5s linear infinite; }
        .cmc-pkt-b { animation: cmc-pkt-b 4.5s linear infinite; }
        .cmc-pkt-c { animation: cmc-pkt-c 4.5s linear infinite; }
        .cmc-out-a { animation: cmc-out-a 4.5s linear infinite; }
        .cmc-out-b { animation: cmc-out-b 4.5s linear infinite; }
      `}</style>

      {/* Background ambience */}
      <div aria-hidden className="absolute inset-0 cmc-bg pointer-events-none" style={{
        background: 'radial-gradient(50% 60% at 20% 30%, rgba(59,130,246,0.18), transparent), radial-gradient(50% 60% at 80% 70%, rgba(255,255,255,0.04), transparent)',
      }} />
      <div aria-hidden className="absolute inset-0 opacity-[0.22] pointer-events-none" style={{
        backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.05) 1px, transparent 1px)',
        backgroundSize: '32px 32px',
        maskImage: 'radial-gradient(ellipse at center, black 55%, transparent 90%)',
      }} />
      <div aria-hidden className="absolute inset-x-0 top-0 h-full pointer-events-none">
        <div className="cmc-scan absolute inset-x-0 h-28" style={{
          background: 'linear-gradient(to bottom, transparent, rgba(59,130,246,0.18), transparent)',
          filter: 'blur(8px)',
        }} />
      </div>

      {/* Header strip */}
      <div className="absolute top-0 left-0 right-0 px-5 pt-4 flex items-center justify-between text-[11px]">
        <div className="inline-flex items-center gap-2 text-neutral-400">
          <span className="w-1.5 h-1.5 rounded-full bg-blue-400 cmc-dot" />
          <span className="uppercase tracking-[0.18em]">Context Model · operational clarity</span>
        </div>
        <span className="text-[10.5px] uppercase tracking-[0.16em] text-neutral-500">live</span>
      </div>

      {/* SVG: lines + particles + central core (covers full area) — DESKTOP ONLY */}
      <svg viewBox="0 0 800 450" className="absolute inset-0 w-full h-full hidden sm:block" preserveAspectRatio="xMidYMid meet" aria-hidden>
        <defs>
          <radialGradient id="cmc-core" cx="50%" cy="50%" r="50%">
            <stop offset="0%"  stopColor="#1e40af" />
            <stop offset="60%" stopColor="#0f1c3a" />
            <stop offset="100%" stopColor="#0a0a0a" />
          </radialGradient>
          <linearGradient id="cmc-edge" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%"  stopColor="#1e3a8a" stopOpacity="0.1" />
            <stop offset="50%" stopColor="#3b82f6" stopOpacity="0.65" />
            <stop offset="100%" stopColor="#1e3a8a" stopOpacity="0.1" />
          </linearGradient>
          <filter id="cmc-glow-f" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2.5" />
          </filter>
        </defs>

        {/* Connector lines from each source to core */}
        {[110, 225, 340].map((y) => (
          <line key={y} x1="200" y1={y} x2="400" y2="225" stroke="url(#cmc-edge)" strokeWidth="1.2" strokeDasharray="3 4" />
        ))}
        {/* Connector lines from core to destinations */}
        {[150, 300].map((y) => (
          <line key={y} x1="400" y1="225" x2="640" y2={y} stroke="url(#cmc-edge)" strokeWidth="1.2" strokeDasharray="3 4" />
        ))}

        {/* Outer pulse rings */}
        <circle cx="400" cy="225" r="72" fill="none" stroke="#3b82f6" strokeWidth="1.2" className="cmc-ring" />
        <circle cx="400" cy="225" r="72" fill="none" stroke="#3b82f6" strokeWidth="1.2" className="cmc-ring" style={{ animationDelay: '1.2s' }} />
        <circle cx="400" cy="225" r="72" fill="none" stroke="#3b82f6" strokeWidth="1.2" className="cmc-ring" style={{ animationDelay: '2.4s' }} />

        {/* Rotating geometry around core */}
        <g className="cmc-rot" style={{ transformOrigin: '400px 225px' }}>
          <polygon
            points="400,160 456,193 456,257 400,290 344,257 344,193"
            fill="none" stroke="#3b82f6" strokeOpacity="0.55" strokeWidth="1.4"
          />
        </g>
        <g className="cmc-rot-r" style={{ transformOrigin: '400px 225px' }}>
          <polygon
            points="400,150 466,190 466,260 400,300 334,260 334,190"
            fill="none" stroke="#60a5fa" strokeOpacity="0.30" strokeWidth="1"
          />
        </g>

        {/* Central core */}
        <g className="cmc-glow">
          <polygon
            points="400,170 450,200 450,250 400,280 350,250 350,200"
            fill="url(#cmc-core)" stroke="#3b82f6" strokeWidth="1.6"
          />
          <text x="400" y="222" textAnchor="middle" fill="#dbeafe" fontSize="13" fontFamily="ui-sans-serif, system-ui" fontWeight="600">Context</text>
          <text x="400" y="238" textAnchor="middle" fill="#93c5fd" fontSize="11" fontFamily="ui-sans-serif, system-ui">Model</text>
        </g>

        {/* Inbound particles */}
        <circle r="4" fill="#60a5fa" filter="url(#cmc-glow-f)" className="cmc-pkt cmc-pkt-a" style={{ offsetPath: "path('M 200 110 L 400 225')" }} />
        <circle r="4" fill="#60a5fa" filter="url(#cmc-glow-f)" className="cmc-pkt cmc-pkt-b" style={{ offsetPath: "path('M 200 225 L 400 225')" }} />
        <circle r="4" fill="#60a5fa" filter="url(#cmc-glow-f)" className="cmc-pkt cmc-pkt-c" style={{ offsetPath: "path('M 200 340 L 400 225')" }} />

        {/* Outbound clarity particles */}
        <circle r="4" fill="#34d399" filter="url(#cmc-glow-f)" className="cmc-pkt cmc-out-a" style={{ offsetPath: "path('M 400 225 L 640 150')" }} />
        <circle r="4" fill="#34d399" filter="url(#cmc-glow-f)" className="cmc-pkt cmc-out-b" style={{ offsetPath: "path('M 400 225 L 640 300')" }} />
      </svg>

      {/* Left sources — overlaid div for typography & icons */}
      <div className="absolute inset-y-0 left-5 w-[34%] hidden sm:block">
        <Source Icon={Workflow}     label="Process data"      sub="ERP · CRM · ITSM events"  top="18%"  delay={0} />
        <Source Icon={BookOpen}     label="Business knowledge" sub="Policies · KPIs · SOPs"    top="44%"  delay={0.6} />
        <Source Icon={BrainCircuit} label="Intelligence"       sub="Models · agents · history"  top="70%"  delay={1.2} />
      </div>

      {/* Right destinations */}
      <div className="absolute inset-y-0 right-5 w-[28%] hidden sm:block">
        <div className="absolute right-0 top-[24%] flex items-center gap-3 justify-end">
          <div className="text-right min-w-0">
            <div className="text-[12.5px] font-semibold text-white">People</div>
            <div className="relative h-[15px] text-[10.5px]">
              <span className="absolute right-0 inline-flex items-center gap-1 text-emerald-300 cmc-tag-1"><Sparkles className="w-3 h-3" /> Reason correctly</span>
              <span className="absolute right-0 inline-flex items-center gap-1 text-emerald-300 cmc-tag-2"><Sparkles className="w-3 h-3" /> Decide sensibly</span>
              <span className="absolute right-0 inline-flex items-center gap-1 text-emerald-300 cmc-tag-3"><Sparkles className="w-3 h-3" /> Act reliably</span>
            </div>
          </div>
          <div className="w-10 h-10 rounded-xl bg-neutral-950 border border-neutral-800 flex items-center justify-center flex-shrink-0">
            <Users className="w-4 h-4 text-emerald-300" />
          </div>
        </div>
        <div className="absolute right-0 top-[64%] flex items-center gap-3 justify-end">
          <div className="text-right min-w-0">
            <div className="text-[12.5px] font-semibold text-white">Agents</div>
            <div className="relative h-[15px] text-[10.5px]">
              <span className="absolute right-0 inline-flex items-center gap-1 text-emerald-300 cmc-tag-2"><Sparkles className="w-3 h-3" /> Reason correctly</span>
              <span className="absolute right-0 inline-flex items-center gap-1 text-emerald-300 cmc-tag-3"><Sparkles className="w-3 h-3" /> Decide sensibly</span>
              <span className="absolute right-0 inline-flex items-center gap-1 text-emerald-300 cmc-tag-1"><Sparkles className="w-3 h-3" /> Act reliably</span>
            </div>
          </div>
          <div className="w-10 h-10 rounded-xl bg-neutral-950 border border-neutral-800 flex items-center justify-center flex-shrink-0">
            <Bot className="w-4 h-4 text-emerald-300" />
          </div>
        </div>
      </div>

      {/* MOBILE-ONLY vertical layout: sources top → core middle → destinations bottom */}
      <div className="absolute inset-0 sm:hidden flex flex-col pt-12 pb-12 px-4">
        {/* Source pills top */}
        <div className="grid grid-cols-3 gap-2">
          {[
            { Icon: Workflow,     l: 'Process',      s: 'ERP · CRM · ITSM' },
            { Icon: BookOpen,     l: 'Knowledge',    s: 'Policies · KPIs' },
            { Icon: BrainCircuit, l: 'Intelligence', s: 'Models · history' },
          ].map((x, i) => (
            <div key={x.l} className="rounded-lg border border-neutral-800 bg-neutral-950 px-2 py-2 flex flex-col items-center gap-1 cmc-source" style={{ animationDelay: `${i * 0.4}s` }}>
              <x.Icon className="w-3.5 h-3.5 text-blue-300" />
              <div className="text-[10.5px] font-semibold text-white leading-none">{x.l}</div>
              <div className="text-[9px] text-neutral-500 leading-none text-center">{x.s}</div>
            </div>
          ))}
        </div>

        {/* Central core (compact SVG) */}
        <div className="flex-1 flex items-center justify-center">
          <svg viewBox="0 0 200 200" className="w-40 h-40" aria-hidden>
            <defs>
              <radialGradient id="cmc-core-m" cx="50%" cy="50%" r="50%">
                <stop offset="0%"  stopColor="#1e40af" />
                <stop offset="60%" stopColor="#0f1c3a" />
                <stop offset="100%" stopColor="#0a0a0a" />
              </radialGradient>
              <linearGradient id="cmc-edge-m" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%"  stopColor="#3b82f6" stopOpacity="0.75" />
                <stop offset="100%" stopColor="#1e3a8a" stopOpacity="0.1" />
              </linearGradient>
              <filter id="cmc-glow-m"><feGaussianBlur stdDeviation="2" /></filter>
            </defs>

            {/* vertical connector hints */}
            <line x1="60"  y1="0" x2="100" y2="80" stroke="url(#cmc-edge-m)" strokeWidth="1" strokeDasharray="3 4" />
            <line x1="100" y1="0" x2="100" y2="80" stroke="url(#cmc-edge-m)" strokeWidth="1" strokeDasharray="3 4" />
            <line x1="140" y1="0" x2="100" y2="80" stroke="url(#cmc-edge-m)" strokeWidth="1" strokeDasharray="3 4" />
            <line x1="70"  y1="200" x2="100" y2="120" stroke="url(#cmc-edge-m)" strokeWidth="1" strokeDasharray="3 4" gradientTransform="rotate(180)" />
            <line x1="130" y1="200" x2="100" y2="120" stroke="url(#cmc-edge-m)" strokeWidth="1" strokeDasharray="3 4" />

            {/* pulse rings */}
            <circle cx="100" cy="100" r="42" fill="none" stroke="#3b82f6" strokeWidth="1.2" className="cmc-ring" />
            <circle cx="100" cy="100" r="42" fill="none" stroke="#3b82f6" strokeWidth="1.2" className="cmc-ring" style={{ animationDelay: '1.2s' }} />

            {/* rotating geometry */}
            <g className="cmc-rot" style={{ transformOrigin: '100px 100px' }}>
              <polygon points="100,65 130,82 130,118 100,135 70,118 70,82" fill="none" stroke="#3b82f6" strokeOpacity="0.55" strokeWidth="1.4" />
            </g>
            <g className="cmc-rot-r" style={{ transformOrigin: '100px 100px' }}>
              <polygon points="100,58 135,80 135,120 100,142 65,120 65,80" fill="none" stroke="#60a5fa" strokeOpacity="0.3" strokeWidth="1" />
            </g>

            {/* central core */}
            <g className="cmc-glow">
              <polygon points="100,75 125,90 125,110 100,125 75,110 75,90" fill="url(#cmc-core-m)" stroke="#3b82f6" strokeWidth="1.4" />
              <text x="100" y="101" textAnchor="middle" fill="#dbeafe" fontSize="9" fontFamily="ui-sans-serif, system-ui" fontWeight="600">Context</text>
              <text x="100" y="113" textAnchor="middle" fill="#93c5fd" fontSize="7" fontFamily="ui-sans-serif, system-ui">Model</text>
            </g>

            {/* inbound particles down */}
            <circle r="2.8" fill="#60a5fa" filter="url(#cmc-glow-m)" className="cmc-pkt cmc-pkt-a" style={{ offsetPath: "path('M 60 0 L 100 80')" }} />
            <circle r="2.8" fill="#60a5fa" filter="url(#cmc-glow-m)" className="cmc-pkt cmc-pkt-b" style={{ offsetPath: "path('M 100 0 L 100 80')" }} />
            <circle r="2.8" fill="#60a5fa" filter="url(#cmc-glow-m)" className="cmc-pkt cmc-pkt-c" style={{ offsetPath: "path('M 140 0 L 100 80')" }} />

            {/* outbound particles down */}
            <circle r="2.8" fill="#34d399" filter="url(#cmc-glow-m)" className="cmc-pkt cmc-out-a" style={{ offsetPath: "path('M 100 120 L 70 200')" }} />
            <circle r="2.8" fill="#34d399" filter="url(#cmc-glow-m)" className="cmc-pkt cmc-out-b" style={{ offsetPath: "path('M 100 120 L 130 200')" }} />
          </svg>
        </div>

        {/* Destination pills bottom */}
        <div className="grid grid-cols-2 gap-2">
          {[
            { Icon: Users, l: 'People' },
            { Icon: Bot,   l: 'Agents' },
          ].map((d, i) => (
            <div key={d.l} className="rounded-lg border border-neutral-800 bg-neutral-950 px-2 py-2 flex items-center gap-2">
              <d.Icon className="w-3.5 h-3.5 text-emerald-300 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-[10.5px] font-semibold text-white leading-none">{d.l}</div>
                <div className="relative h-[12px] text-[9px] mt-1">
                  <span className={`absolute left-0 inline-flex items-center gap-1 text-emerald-300 ${i === 0 ? 'cmc-tag-1' : 'cmc-tag-2'}`}><Sparkles className="w-2.5 h-2.5" /> Reason correctly</span>
                  <span className={`absolute left-0 inline-flex items-center gap-1 text-emerald-300 ${i === 0 ? 'cmc-tag-2' : 'cmc-tag-3'}`}><Sparkles className="w-2.5 h-2.5" /> Decide sensibly</span>
                  <span className={`absolute left-0 inline-flex items-center gap-1 text-emerald-300 ${i === 0 ? 'cmc-tag-3' : 'cmc-tag-1'}`}><Sparkles className="w-2.5 h-2.5" /> Act reliably</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom caption */}
      <div className="absolute bottom-3 left-5 right-5 sm:left-auto sm:right-5 sm:w-auto sm:flex sm:justify-end text-center sm:text-right pointer-events-none">
        <p className="text-[10px] sm:text-[11px] text-neutral-500 uppercase tracking-[0.16em]">operational clarity · in motion</p>
      </div>
    </div>
  );
};

export default ContextModelClip;
